
import React, {
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import { useNavigate } from "react-router-dom";

import {
  ClearRounded,
  EditRounded,
  VisibilityRounded,
} from "@mui/icons-material";

import {
  Box,
  Button,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";

import { Alert, Dialog, Table } from "../../../layout";

import * as actions from "../../../api/actions/pos";
import {
  controller,
  endpoints,
} from "../../../api/actions/api";

import { AppContext } from "../../../App";
import { IsEmptyObj } from "../../../hooks/Method";
import { formatCurrency } from "../../../hooks/Currency";

import ViewReturnItems from "./ViewReturnItems";
import ReportPrintButton from "../../reports/ReportPrintButton";

//------------------------------------------------
// Purchase Returns
//------------------------------------------------
const PurchReturn = (props) => {
  const { t } = useTranslation();

  const {
    _list,
    _type,
    _clear,
    _getById,
    _delete,
    _store,
  } = props;

  const navigate = useNavigate();
  const { setLoader } = useContext(AppContext);

  const reportRef = useRef(null);

  const [showForm, setShowForm] = useState(false);
  const [viewData, setViewData] = useState({});

  const [alert, setAlert] = useState({
    isOpen: false,
    type: "",
    title: "",
    subTitle: "",
  });

  //------------------------------------------------
  // Load Purchase Returns
  //------------------------------------------------
  useEffect(() => {
    if (_store?.id) {
      setLoader(true);

      _getById(
        endpoints.PurchReturn + controller.GetByStore,
        _store.id,
        actions.ActionType.List,
        () => setLoader(false)
      );
    }

    return () => {
      _clear();
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [_store?.id]);

  //------------------------------------------------
  // Purchase Returns Table Data
  //------------------------------------------------
  const tableData = useMemo(() => {
    if (
      _type !== "purchReturns" ||
      !Array.isArray(_list)
    ) {
      return [];
    }

    return _list.map((obj) => {
      const purchase = obj.purchase || {};
      const returnItems = Array.isArray(
        obj.purchReturnItems
      )
        ? obj.purchReturnItems
        : [];

      const totalItem = returnItems.reduce(
        (total, item) =>
          total + Number(item.qty || 0),
        0
      );

      const returnAmount = returnItems.reduce(
        (total, item) =>
          total +
          Number(item.qty || 0) *
            Number(item.product?.purchPrice || 0),
        0
      );

      return {
        ...obj,

        supplier: purchase.supplier?.company || "",
        reference: purchase.id ?? "",

        totalItem,

        returnValue: formatCurrency(
          returnAmount,
          _store
        ),

        viewReceipt: purchase.receipt ? (
          <a
            href={purchase.receipt}
            target="_blank"
            rel="noreferrer"
          >
            {t("viewReceipt")}
          </a>
        ) : (
          ""
        ),
      };
    });
  }, [_list, _type, _store, t]);

  //------------------------------------------------
  // Delete Purchase Return
  //------------------------------------------------
  const handleDlt = (id) => {
    setAlert({
      isOpen: true,
      type: "confirmation",
      title: t("areYouSure"),
      subTitle: t("deleteRecordConfirmation"),

      onConfirm: () => {
        const onComplete = (res) => {
          if (res.status === 200) {
            setAlert({
              isOpen: true,
              type: "success",
              title: t("deletedSuccessfully"),
              subTitle: res.msg,
            });
          } else {
            setAlert({
              isOpen: true,
              type: "error",
              title: t("error"),
              subTitle: res.msg,
            });
          }
        };

        _delete(
          endpoints.PurchReturn,
          id,
          actions.ActionType.RmvList,
          onComplete
        );
      },
    });
  };

  //------------------------------------------------
  // View Purchase Return Details
  //------------------------------------------------
  const handleView = (data) => {
    // Pass original API data to ViewReturnItems.
    const original = _list.find(
      (item) => String(item.id) === String(data.id)
    );

    setViewData(original || data);
    setShowForm(true);
  };

  //------------------------------------------------
  // Table Columns
  //------------------------------------------------
  const screenColumns = [
    { id: "actions", label: t("actions") },
    { id: "reference", label: t("referenceNo") },
    { id: "supplier", label: t("supplier") },
    { id: "totalItem", label: t("totalItem") },
    { id: "returnValue", label: t("returnValue") },
    { id: "returnDate", label: t("returnDate") },
    { id: "viewReceipt", label: t("receipt") },
  ];

  const printColumns = screenColumns.filter(
    (column) =>
      column.id !== "actions" &&
      column.id !== "viewReceipt"
  );

  //------------------------------------------------
  // Action Buttons
  //------------------------------------------------
  const ActionComp = (data) => (
    <Box sx={{ display: "flex", gap: 0.5 }}>
      {/* View */}
      <Tooltip arrow title={t("view")}>
        <IconButton
          onClick={() => handleView(data)}
          size="small"
          className="btn-primary mr-5"
        >
          <VisibilityRounded className="fs-18 m-2" />
        </IconButton>
      </Tooltip>

      {/* Edit */}
      <Tooltip arrow title={t("editThisItem")}>
        <IconButton
          onClick={() =>
            navigate(`purchase-return-form/${data.id}`)
          }
          size="small"
          className="btn-primary mr-5"
        >
          <EditRounded className="fs-18 m-2" />
        </IconButton>
      </Tooltip>

      {/* Delete */}
      <Tooltip arrow title={t("deleteThisItem")}>
        <IconButton
          onClick={() => handleDlt(data.id)}
          size="small"
          className="btn-error"
        >
          <ClearRounded className="fs-18 m-2" />
        </IconButton>
      </Tooltip>
    </Box>
  );

  //------------------------------------------------
  // Add Purchase Return Button
  //------------------------------------------------
  const HeaderComp = () => (
    <Button
      onClick={() => navigate("purchase-return-form")}
      className="btn-secondary"
      sx={{
        width: { md: "250px", xs: "100%" },
        mb: { xs: "10px", md: "0px" },
        fontSize: "17px",
        padding: "8px",
      }}
    >
      {t("addPurchaseReturn")}
    </Button>
  );

  //------------------------------------------------
  // Render
  //------------------------------------------------
  return (
    <>
      {/* Header and Print Preview */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 2,
          mb: 2,
        }}
      >
        <Typography variant="h5">
          {t("purchaseReturns")}
        </Typography>

        <ReportPrintButton
          reportRef={reportRef}
          paperSize="Letter"
          reportTitle={t("purchaseReturns")}
        />
      </Box>

      {/* Normal Purchase Returns Table */}
      <Table
        ActionComp={ActionComp}
        HeaderComp={HeaderComp}
        tblHead={screenColumns}
        tblData={tableData}
      />

      {/* Printable Purchase Returns List */}
      <Box
        ref={reportRef}
        className="purchase-returns-print"
        sx={{
          position: "absolute",
          left: "-10000px",
          top: 0,
          width: "1000px",
          backgroundColor: "#fff",
          color: "#000",
          p: 2,
        }}
      >
        <Typography
          variant="h5"
          sx={{
            textAlign: "center",
            fontWeight: "bold",
            mb: 2,
          }}
        >
          {t("purchaseReturns")}
        </Typography>

        <Table
          printMode={true}
          ActionComp={() => <div />}
          HeaderComp={() => <div />}
          tblHead={printColumns}
          tblData={tableData}
        />
      </Box>

      {/* Alert */}
      <Alert
        alert={alert}
        setAlert={setAlert}
      />

      {/* Existing Purchase Return Details Dialog */}
      <Dialog
        show={showForm}
        maxWidth="lg"
        onClose={false}
      >
        {!IsEmptyObj(viewData) && (
          <ViewReturnItems
            setShowForm={setShowForm}
            viewData={viewData}
          />
        )}
      </Dialog>
    </>
  );
};

const mapStateToProps = (state) => ({
  _list: state.pos.list,
  _type: state.pos.type,
  _store: state.pos.defStore,
});

const mapActionToProps = {
  _getById: actions.getById,
  _delete: actions.Delete,
  _clear: actions.clear,
};

export default connect(
  mapStateToProps,
  mapActionToProps
)(PurchReturn);
